import React, { useState, useCallback } from 'react';
import {
  Card, Stack, Group, Text, Button, Textarea, Badge,
  ThemeIcon, Progress, Divider, Alert
} from '@mantine/core';
import {
  IconCheck, IconPencil, IconRotateClockwise,
  IconSparkles, IconAlertTriangle, IconCompass, IconArrowRight
} from '@tabler/icons-react';
import AIGeneratingIcon from '../../components/AIGeneratingIcon';
import { authFetch } from '../../utils/authFetch';

// Explorer organizational risk areas (constant - defined outside component to avoid re-creation)
const EXPLORER_AREAS = [
  'commercialRisk', 'operationalRisk', 'complianceRisk',
  'societalRisk', 'environmentalRisk', 'reputationalRisk'
];

/**
 * ExplorerNarrativeCard - Narrative card for Explorer assessments
 * Generates organizational narrative from business context + use case.
 * (Inherited-risks panel removed — Explorer is treated as standalone in
 * the narrative phase; any inheritance is handled by the autopilot.)
 */
export default function ExplorerNarrativeCard({
  modelId,
  assessmentUuid,
  parentAssessmentUuid,
  bucket,
  path,
  businessContext: initialBusinessContext,
  useCase: initialUseCase,
  onApproved
}) {
  // Step: context | generating | ready | approved | failed
  const [status, setStatus] = useState('context');
  const [narrative, setNarrative] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [focusAreas, setFocusAreas] = useState([]);

  // Local context form state (editable before generation)
  const [localBusinessContext, setLocalBusinessContext] = useState(initialBusinessContext || '');
  const [localUseCase, setLocalUseCase] = useState(initialUseCase || '');

  // Generate Explorer narrative with inherited context
  const generateNarrative = useCallback(async () => {
    try {
      setStatus('generating');
      setKeywords([]);
      setNarrative('');

      console.log('[ExplorerNarrativeCard] Generating Explorer narrative with payload:', {
        modelId,
        assessmentUuid,
        parentAssessmentUuid,
        bucket,
        path,
        hasBusinessContext: !!localBusinessContext,
        hasUseCase: !!localUseCase
      });

      const res = await authFetch('/.netlify/functions/explorerGenerateNarrative', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          parentAssessmentUuid,
          bucket,
          path,
          businessContext: localBusinessContext,
          useCase: localUseCase
        })
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => 'Generation failed');
        throw new Error(errorText || 'Request failed');
      }

      const data = await res.json();
      if (data.success && data.narrative) {
        setNarrative(data.narrative);
        setFocusAreas(data.focusAreas || []);

        // Generate keywords from Explorer areas and focus areas
        const displayKeywords = [
          ...EXPLORER_AREAS.map(a => a.replace('Risk', '')),
          ...(data.focusAreas || []).slice(0, 5)
        ].slice(0, 12);
        setKeywords(displayKeywords);
        setStatus('ready');
      } else {
        throw new Error(data.error || 'Narrative generation failed');
      }
    } catch (error) {
      console.error('[ExplorerNarrativeCard] Generation error:', error);
      setStatus('failed');
      setNarrative('');
    }
  }, [modelId, assessmentUuid, parentAssessmentUuid, bucket, path, localBusinessContext, localUseCase]);

  // Handler for submitting context form and starting generation
  const handleSubmitContext = useCallback(() => {
    if (!modelId || !assessmentUuid) {
      console.error('[ExplorerNarrativeCard] Missing modelId or assessmentUuid');
      return;
    }
    generateNarrative();
  }, [modelId, assessmentUuid, generateNarrative]);

  const handleApprove = () => {
    setStatus('approved');
    onApproved?.(narrative, focusAreas);
  };

  const handleRegenerate = () => {
    setStatus('context'); // Go back to context form to allow editing
    setNarrative('');
  };

  const statusBadge = (() => {
    switch (status) {
      case 'context':
        return <Badge variant="light" color="blue">Enter Context</Badge>;
      case 'generating':
        return (
          <Badge variant="light" color="blue">
            <Group gap="xs">
              <AIGeneratingIcon isGenerating className="ai-pulse-strong" />
              <Text size="sm">Generating...</Text>
            </Group>
          </Badge>
        );
      case 'ready':
        return <Badge variant="light" color="grape">Ready for Review</Badge>;
      case 'approved':
        return (
          <Badge variant="filled" color="grape">
            <Group gap="xs">
              <IconCheck size={14} />
              <Text size="sm">Approved</Text>
            </Group>
          </Badge>
        );
      case 'failed':
        return <Badge variant="light" color="red">Generation Failed</Badge>;
      default:
        return <Badge variant="outline" color="gray">Loading...</Badge>;
    }
  })();

  const borderColor = ({
    context: '#3b82f6',
    generating: '#9C36B5',
    ready: '#9C36B5',
    approved: '#862E9C',
    failed: '#dc2626',
    loading: '#6b7280',
  })[status] || '#6b7280';

  const canEdit = status === 'ready' || status === 'approved';
  const canApprove = status === 'ready' || status === 'approved';
  const progress = status === 'approved' ? 100 : (status === 'ready' ? 100 : (status === 'generating' ? 50 : (status === 'context' ? 10 : 25)));

  return (
    <Stack gap="lg">
      {/* Header with Progress */}
      <Card withBorder p="lg" style={{ backgroundColor: 'rgba(174, 62, 201, 0.05)' }}>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="md" align="center">
                <ThemeIcon size="xl" variant="gradient" gradient={{ from: 'grape', to: 'violet' }}>
                  <IconCompass size={24} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text fw={600} size="lg">Explorer Risk Analysis</Text>
                  <Text size="sm" c="dimmed">
                    Assessing business and risk impacts
                  </Text>
                </Stack>
              </Group>
            </div>
            {statusBadge}
          </Group>
          <Progress value={progress} size="sm" color={status === 'context' ? 'blue' : 'grape'} />
        </Stack>
      </Card>

      {/* Context Form - Shown before narrative generation */}
      {status === 'context' && (
        <Card withBorder p="lg" style={{ borderColor: '#3b82f6' }}>
          <Stack gap="md">
            <Group gap="md">
              <ThemeIcon size="lg" variant="light" color="blue">
                <IconPencil size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text fw={600}>Business Context & Use Case</Text>
                <Text size="sm" c="dimmed">
                  Provide context to guide the Explorer risk analysis
                </Text>
              </Stack>
            </Group>

            <Divider />

            <Stack gap="md">
              <Textarea
                label="Business Context"
                description="Describe the business environment, stakeholders, and organizational context"
                placeholder="e.g., Our organization is a mid-sized financial services company. Key stakeholders include regulatory compliance, risk management, and customer service departments..."
                value={localBusinessContext}
                onChange={(e) => setLocalBusinessContext(e.target.value)}
                minRows={4}
                autosize
              />

              <Textarea
                label="Use Case Description"
                description="Describe how the AI system will be used in your organization"
                placeholder="e.g., The AI system will be used to automate customer support inquiries, route complex cases to human agents, and provide real-time analytics to management..."
                value={localUseCase}
                onChange={(e) => setLocalUseCase(e.target.value)}
                minRows={4}
                autosize
              />
            </Stack>

            <Group justify="flex-end" mt="md">
              <Button
                color="blue"
                rightSection={<IconArrowRight size={16} />}
                onClick={handleSubmitContext}
              >
                Generate Narrative
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {/* Focus Areas - only shown after context is submitted */}
      {status !== 'context' && focusAreas.length > 0 && (
        <Card withBorder p="md">
          <Stack gap="xs">
            <Text size="sm" fw={500}>Suggested Focus Areas</Text>
            <Group gap="xs">
              {focusAreas.map((area, idx) => (
                <Badge key={idx} variant="light" color="grape" size="sm">
                  {area.replace(/([A-Z])/g, ' $1').replace('Risk', '').trim()}
                </Badge>
              ))}
            </Group>
          </Stack>
        </Card>
      )}

      {/* Narrative Content - only shown after context is submitted */}
      {status !== 'context' && (
      <Card withBorder p="lg" style={{ borderColor }}>
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="grape">
                <IconSparkles size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>Risk Narrative</Text>
            </Group>
            {canEdit && (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPencil size={14} />}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Cancel Edit' : 'Edit'}
              </Button>
            )}
          </Group>

          {status === 'generating' && (
            <Stack align="center" py="xl">
              <AIGeneratingIcon isGenerating size={48} />
              <Text size="sm" c="dimmed">Analyzing context...</Text>
            </Stack>
          )}

          {status === 'failed' && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              <Stack gap="xs">
                <Text size="sm">Failed to generate narrative. Please try again.</Text>
                <Button
                  variant="light"
                  color="red"
                  size="xs"
                  onClick={handleRegenerate}
                >
                  Retry
                </Button>
              </Stack>
            </Alert>
          )}

          {(status === 'ready' || status === 'approved') && (
            <>
              {isEditing ? (
                <Textarea
                  value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  minRows={10}
                  autosize
                />
              ) : (
                <Text
                  size="sm"
                  style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}
                >
                  {narrative}
                </Text>
              )}

              {/* Keywords */}
              {keywords.length > 0 && (
                <>
                  <Divider />
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">Keywords:</Text>
                    {keywords.map((kw, idx) => (
                      <Badge key={idx} size="xs" variant="outline" color="grape">
                        {kw}
                      </Badge>
                    ))}
                  </Group>
                </>
              )}

              {/* Actions */}
              <Group justify="space-between" mt="md">
                <Button
                  variant="light"
                  size="sm"
                  leftSection={<IconRotateClockwise size={16} />}
                  onClick={handleRegenerate}
                  disabled={status === 'approved'}
                >
                  Regenerate
                </Button>
                {canApprove && status !== 'approved' && (
                  <Button
                    color="grape"
                    size="sm"
                    leftSection={<IconCheck size={16} />}
                    onClick={handleApprove}
                  >
                    Approve & Continue
                  </Button>
                )}
              </Group>
            </>
          )}
        </Stack>
      </Card>
      )}
    </Stack>
  );
}
